import { useEffect, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { PageHeader } from "@/widgets/page-header"
import { Modal } from "@/shared/ui/modal"
import { ConfirmModal } from "@/shared/ui/confirm-modal"
import { ActionButton } from "@/shared/ui/action-button"
import {
    getSocialProgramById,
    getSocialProgramGroups,
    createSocialProgramGroup,
    deleteSocialProgramGroup,
    removeUserFromSocialGroup,
    addMemberToGroup,
    createGroupInvitation,
    getGroupInvitations,
    deleteGroupInvitation,
    ISocialProgram,
    ISocialProgramGroup,
    IInvitation
} from "@/shared/api/social-programs"
import { getUsers } from "@/shared/api/users"
import { getDisplayName } from "@/entities/user"
import { Loader } from "@/shared/ui/loader"
import notify from "@/shared/lib/notify"
import { getErrorMessage } from "@/shared/consts/error-codes"
import AsyncSelect from "react-select/async"
import ArrowDown from "@/shared/assets/images/ArrowDown.svg"
import TrashSvg from "@/shared/assets/images/Trash.svg"
import ProhibitSvg from "@/shared/assets/images/Prohibit.svg"
import ClipboardTextSvg from "@/shared/assets/images/ClipboardText.svg"
import UserCirclePlusSvg from "@/shared/assets/images/UserCirclePlus.svg"
import LinkSvg from "@/shared/assets/images/Link.svg"
import PlusSvg from "@/shared/assets/images/Plus.svg"

type UserOption = { value: string; label: string }

const loadUserOptions = (inputValue: string): Promise<UserOption[]> =>
    getUsers({ page: 1, limit: 50, search: inputValue || undefined, all: true })
        .then(r => r.data.items.map(u => ({ value: u.id, label: getDisplayName(u) })))
        .catch((): UserOption[] => [])

interface ConfirmState {
    title: string
    message: string
    confirmText: string
    onConfirm: () => Promise<void>
}

export const SocialProgramGroupsDetailPage = () => {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()

    const [program, setProgram] = useState<ISocialProgram | null>(null)
    const [groups, setGroups] = useState<ISocialProgramGroup[]>([])
    const [isLoading, setIsLoading] = useState(true)

    const [isCreateOpen, setIsCreateOpen] = useState(false)
    const [newGroupName, setNewGroupName] = useState('')
    const [creatingGroup, setCreatingGroup] = useState(false)

    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
    const [invitations, setInvitations] = useState<Record<string, IInvitation[]>>({})
    const [invitationsLoading, setInvitationsLoading] = useState<Set<string>>(new Set())
    const [creatingInvitation, setCreatingInvitation] = useState<Set<string>>(new Set())

    const [addMemberModal, setAddMemberModal] = useState<{ groupId: string; groupName: string } | null>(null)
    const [addMemberSelection, setAddMemberSelection] = useState<UserOption | null>(null)
    const [addMemberLoading, setAddMemberLoading] = useState(false)

    const [confirmState, setConfirmState] = useState<ConfirmState | null>(null)

    const reloadGroups = async () => {
        if (!id) return
        const res = await getSocialProgramGroups(id)
        setGroups(res.data)
    }

    const loadAll = async () => {
        if (!id) return
        setIsLoading(true)
        try {
            const [programRes, groupsRes] = await Promise.all([
                getSocialProgramById(id),
                getSocialProgramGroups(id)
            ])
            setProgram(programRes.data)
            setGroups(groupsRes.data)
        } catch {
            notify.error('Не удалось загрузить данные')
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        loadAll()
    }, [id])

    const handleCreateGroup = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!id || !newGroupName.trim()) return
        setCreatingGroup(true)
        try {
            await createSocialProgramGroup(id, newGroupName.trim())
            notify.success('Группа создана')
            setNewGroupName('')
            setIsCreateOpen(false)
            await reloadGroups()
        } catch (err) {
            notify.error(getErrorMessage(err))
        } finally {
            setCreatingGroup(false)
        }
    }

    const handleDeleteGroup = (groupId: string, groupName: string) => {
        setConfirmState({
            title: 'Удалить группу',
            message: `Группа «${groupName}» и все её участники будут удалены. Продолжить?`,
            confirmText: 'Удалить',
            onConfirm: async () => {
                await deleteSocialProgramGroup(id!, groupId)
                notify.success('Группа удалена')
                setGroups(prev => prev.filter(g => g.id !== groupId))
                setExpandedGroups(prev => { const s = new Set(prev); s.delete(groupId); return s })
            },
        })
    }

    const handleRemoveMember = (groupId: string, userId: string, userName: string) => {
        setConfirmState({
            title: 'Исключить участника',
            message: `«${userName}» будет исключён из группы. Продолжить?`,
            confirmText: 'Исключить',
            onConfirm: async () => {
                await removeUserFromSocialGroup(groupId, userId)
                notify.success('Участник исключён')
                await reloadGroups()
            },
        })
    }

    const openAddMemberModal = (groupId: string, groupName: string) => {
        setAddMemberSelection(null)
        setAddMemberModal({ groupId, groupName })
    }

    const closeAddMemberModal = () => {
        setAddMemberModal(null)
        setAddMemberSelection(null)
    }

    const handleAddMember = async () => {
        if (!id || !addMemberModal || !addMemberSelection) return
        setAddMemberLoading(true)
        try {
            await addMemberToGroup(id, addMemberModal.groupId, addMemberSelection.value)
            notify.success('Участник добавлен')
            closeAddMemberModal()
            await reloadGroups()
        } catch (err) {
            notify.error(getErrorMessage(err))
        } finally {
            setAddMemberLoading(false)
        }
    }

    const handleCreateInvitation = async (groupId: string) => {
        if (!id) return
        setCreatingInvitation(prev => new Set(prev).add(groupId))
        try {
            await createGroupInvitation(id, groupId)
            notify.success('Ссылка создана')
            const res = await getGroupInvitations(id, groupId)
            setInvitations(prev => ({ ...prev, [groupId]: res.data }))
        } catch (err) {
            notify.error(getErrorMessage(err))
        } finally {
            setCreatingInvitation(prev => { const s = new Set(prev); s.delete(groupId); return s })
        }
    }

    const handleCopyLink = (link: string) => {
        navigator.clipboard.writeText(link)
        notify.success('Ссылка скопирована')
    }

    const handleDeleteInvitation = (groupId: string, invitationId: string) => {
        setConfirmState({
            title: 'Удалить ссылку',
            message: 'Пригласительная ссылка будет удалена без возможности восстановления.',
            confirmText: 'Удалить',
            onConfirm: async () => {
                await deleteGroupInvitation(id!, groupId, invitationId)
                notify.success('Ссылка удалена')
                setInvitations(prev => ({
                    ...prev,
                    [groupId]: prev[groupId].filter(inv => inv.id !== invitationId)
                }))
            },
        })
    }

    const toggleGroup = async (groupId: string) => {
        const next = new Set(expandedGroups)
        if (next.has(groupId)) {
            next.delete(groupId)
        } else {
            next.add(groupId)
            if (invitations[groupId] === undefined) {
                setInvitationsLoading(prev => new Set(prev).add(groupId))
                try {
                    const res = await getGroupInvitations(id!, groupId)
                    setInvitations(prev => ({ ...prev, [groupId]: res.data }))
                } catch {
                    setInvitations(prev => ({ ...prev, [groupId]: [] }))
                } finally {
                    setInvitationsLoading(prev => { const s = new Set(prev); s.delete(groupId); return s })
                }
            }
        }
        setExpandedGroups(next)
    }

    if (isLoading) {
        return (
            <div className="_page_container">
                <PageHeader title="Группы" />
                <div className="info_panel"><Loader /></div>
            </div>
        )
    }

    return (
        <div className="_page_container">
            <PageHeader title={'Социальные группы'} />

            <div className="flexbox">
                <button
                    onClick={() => navigate(`/social-programs/${id}`)}
                    className="flex items-center gap-1 text-sm font-semibold text-color-dark self-start"
                >
                    ← Назад к программе
                </button>

                <div className="flex items-center justify-between">
                    <span className="font-mediumttf text-lg xxl:text-[20px] screen:text-2xl uppercase">
                        Группы
                        {groups.length > 0 && (
                            <span className="ml-2 text-base font-semibold text-color-secondary">
                                {groups.length}
                            </span>
                        )}
                    </span>
                    <button
                        className="form_handle_button !w-auto px-5"
                        onClick={() => setIsCreateOpen(true)}
                    >
                        + Создать группу
                    </button>
                </div>

                {groups.length === 0 && (
                    <div className="box text-sm text-color-secondary">Групп пока нет</div>
                )}

                {groups.map(group => (
                    <div key={group.id} className="box !p-0 overflow-hidden">

                        {/* Group header */}
                        <div
                            className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-black/[0.02] transition select-none"
                            onClick={() => toggleGroup(group.id)}
                        >
                            <div className="flex items-center gap-3">
                                <span className="font-semibold text-base">{group.name}</span>
                                <span className="text-xs font-semibold text-color-secondary bg-black/5 rounded-full px-2.5 py-0.5">
                                    {group.users.length} уч.
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                <ActionButton
                                    onClick={e => { e.stopPropagation(); handleDeleteGroup(group.id, group.name) }}
                                    icon={TrashSvg}
                                    title="Удалить группу"
                                />
                                <span className="text-color-secondary text-xs w-4 text-center">
                                    {expandedGroups.has(group.id) ? '▲' : '▼'}
                                </span>
                            </div>
                        </div>

                        {/* Expanded content */}
                        {expandedGroups.has(group.id) && (
                            <div className="border-t divide-y">

                                {/* Members section */}
                                <div className="px-5 py-4">
                                    <div className="flex items-center justify-between mb-3">
                                        <span className="font-semibold text-xs uppercase tracking-wide text-color-secondary">
                                            Участники
                                        </span>
                                        <button
                                            onClick={() => openAddMemberModal(group.id, group.name)}
                                            className="flex items-center gap-1.5 text-xs font-semibold text-color-dark border border-color-dark rounded-full px-3 py-1 hover:bg-black/5 transition"
                                        >
                                            <UserCirclePlusSvg width={14} height={14} />
                                            Добавить участника
                                        </button>
                                    </div>

                                    {group.users.length === 0 ? (
                                        <p className="text-sm text-color-secondary py-1">Нет участников</p>
                                    ) : (
                                        <div className="flex flex-col divide-y">
                                            {group.users.map(user => (
                                                <div key={user.id} className="flex items-center justify-between py-2.5">
                                                    <div className="text-sm">
                                                        <span className="font-medium">
                                                            {user.name || `@${user.username}`}
                                                        </span>
                                                        {user.name && (
                                                            <span className="text-color-secondary ml-1.5 text-xs">
                                                                @{user.username}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <ActionButton
                                                        onClick={() => handleRemoveMember(group.id, user.id, user.name || `@${user.username}`)}
                                                        icon={ProhibitSvg}
                                                        title="Исключить из группы"
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Invitations section */}
                                <div className="px-5 py-4">
                                    <div className="flex items-center justify-between mb-3">
                                        <span className="font-semibold text-xs uppercase tracking-wide text-color-secondary">
                                            Пригласительные ссылки
                                        </span>
                                        <button
                                            onClick={() => handleCreateInvitation(group.id)}
                                            disabled={creatingInvitation.has(group.id)}
                                            className="flex items-center gap-1.5 text-xs font-semibold text-color-dark border border-color-dark rounded-full px-3 py-1 hover:bg-black/5 transition disabled:opacity-40 disabled:cursor-not-allowed"
                                        >
                                            <PlusSvg width={12} height={12} />
                                            {creatingInvitation.has(group.id) ? 'Создание...' : 'Создать ссылку'}
                                        </button>
                                    </div>

                                    {invitationsLoading.has(group.id) && (
                                        <div className="py-2"><Loader /></div>
                                    )}

                                    {!invitationsLoading.has(group.id) && (invitations[group.id] ?? []).length === 0 && (
                                        <p className="text-sm text-color-secondary py-1">Нет ссылок</p>
                                    )}

                                    {!invitationsLoading.has(group.id) && (
                                        <div className="flex flex-col gap-2">
                                            {(invitations[group.id] ?? []).map(inv => (
                                                <div key={inv.id} className="flex items-center gap-3 bg-black/[0.03] rounded-xl px-3 py-2.5">
                                                    <LinkSvg width={16} height={16} className="shrink-0 text-color-secondary" />
                                                    <div className="flex-1 min-w-0">
                                                        <div className="text-xs font-medium text-color-dark truncate">
                                                            {inv.redirect_link}
                                                        </div>
                                                        <div className="flex items-center gap-2 mt-0.5">
                                                            <span className="text-xs text-color-secondary">
                                                                {inv.total_redirects} переходов
                                                            </span>
                                                            <span className={`text-[10px] font-semibold uppercase rounded-full px-2 py-0.5 ${inv.permanent ? 'bg-color-primary/20 text-color-dark' : 'bg-black/10 text-color-secondary'}`}>
                                                                {inv.permanent ? 'Многоразовая' : 'Одноразовая'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-1 shrink-0">
                                                        <ActionButton
                                                            onClick={() => handleCopyLink(inv.redirect_link)}
                                                            icon={ClipboardTextSvg}
                                                            title="Скопировать ссылку"
                                                        />
                                                        <ActionButton
                                                            onClick={() => handleDeleteInvitation(group.id, inv.id)}
                                                            icon={TrashSvg}
                                                            title="Удалить ссылку"
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Create group modal */}
            <Modal
                isOpen={isCreateOpen}
                title="Создать группу"
                onRequestClose={() => { setIsCreateOpen(false); setNewGroupName('') }}
            >
                <form onSubmit={handleCreateGroup} className="flex flex-col gap-4">
                    <div className="flex flex-col gap-2 pb-[10px] border-b-[1.5px] border-[#B9C9C6]">
                        <label className="font-semibold uppercase text-[12px] text-[#828282]">Название</label>
                        <input
                            className="w-full outline-none text-lg [&::placeholder]:text-color-dark/70 [&::placeholder]:text-base text-color-dark"
                            placeholder="Введите название группы"
                            value={newGroupName}
                            onChange={e => setNewGroupName(e.target.value)}
                            required
                            autoFocus
                        />
                    </div>
                    <div className="flex gap-3">
                        <button
                            type="button"
                            className="form_cancel_button"
                            onClick={() => { setIsCreateOpen(false); setNewGroupName('') }}
                        >
                            Отмена
                        </button>
                        <button type="submit" className="form_handle_button" disabled={creatingGroup}>
                            {creatingGroup ? 'Создание...' : 'Создать'}
                        </button>
                    </div>
                </form>
            </Modal>

            {/* Add member modal */}
            <Modal
                isOpen={!!addMemberModal}
                title="Добавить участника"
                subtitle={addMemberModal ? `Группа «${addMemberModal.groupName}»` : undefined}
                onRequestClose={closeAddMemberModal}
            >
                <div className="flex flex-col gap-4">
                    <div className={`flex flex-col gap-[6px] pb-[8px] border-b-[1.5px] border-[#B9C9C6]`}>
                        <label className="font-semibold uppercase text-base text-[#828282]">
                            Пользователь
                        </label>
                        <AsyncSelect
                            classNames={{
                                control: () => 'w-full !outline-0 !shadow-none !min-h-[auto] !border-0 !border-none !p-0 !outline-none',
                                valueContainer: () => '!border-0 !outline-0 !p-0 text-color-dark text-lg',
                                placeholder: () => 'text-color-dark/70 text-base !m-0 text-color-dark',
                                menu: () => '!left-[0]',
                                input: () => '!m-0 !p-0',
                                menuPortal: () => '!z-[9999]',
                            }}
                            loadOptions={loadUserOptions}
                            value={addMemberSelection}
                            onChange={option => setAddMemberSelection(option as UserOption | null)}
                            placeholder="Введите имя или телефон..."
                            noOptionsMessage={() => 'Начните вводить для поиска'}
                            menuPosition="fixed"
                            menuPortalTarget={document.body}
                            components={{
                                DropdownIndicator: () => <ArrowDown height={20} width={20} />,
                                IndicatorSeparator: () => null,
                            }}
                        />
                    </div>
                    <div className="flex gap-3">
                        <button type="button" className="form_cancel_button" onClick={closeAddMemberModal}>
                            Отмена
                        </button>
                        <button
                            className="form_handle_button"
                            disabled={!addMemberSelection || addMemberLoading}
                            onClick={handleAddMember}
                        >
                            {addMemberLoading ? 'Добавление...' : 'Добавить'}
                        </button>
                    </div>
                </div>
            </Modal>

            <ConfirmModal
                isOpen={!!confirmState}
                title={confirmState?.title ?? ''}
                message={confirmState?.message ?? ''}
                confirmText={confirmState?.confirmText}
                onConfirm={async () => {
                    await confirmState?.onConfirm()
                    setConfirmState(null)
                }}
                onCancel={() => setConfirmState(null)}
            />
        </div>
    )
}
